/**
 * Unified FE gate for quick-edit entrypoint visibility.
 *
 * Consolidates the three conditions that must ALL hold for the admin
 * quick-edit FAB to appear on a page:
 *   1. User has ADMIN, OWNER, or WEB_EDITOR role.
 *   2. `mode.editor` feature flag is enabled for the current domain.
 *   3. The current page slug is registered in the editable-pages config.
 *
 * This is a UX-only gate — server-side RBAC remains the authority for
 * actual write/publish protection.
 */

import { findEditablePage } from '@/configs/editable-pages-config'
import { hasAdminDashboardAccess, Role } from '@/enums'

/**
 * Check whether a user can open the page/template editor.
 * Allows ADMIN, OWNER, and WEB_EDITOR.
 * Returns `false` for `undefined`/`null` users or missing roles.
 */
export function hasPageEditorRole(user: { roles?: Role[] } | null | undefined): boolean {
  if (!user?.roles) return false
  return hasAdminDashboardAccess(user.roles)
}

/**
 * Check whether a slug is registered in the editable-pages config.
 */
export function isEditablePageSlug(slug: string): boolean {
  return findEditablePage(slug) !== undefined
}

export interface QuickEditGateInput {
  /** Current authenticated user (may be undefined when logged out) */
  user: { roles?: Role[] } | null | undefined
  /** Result of `useCan('mode.editor')` — domain-level editor toggle */
  canEdit: boolean
  /** Slug of the page being viewed (e.g. "homepage", "learning-path") */
  slug: string
}

/**
 * Single gate that determines whether the quick-edit FAB should be
 * rendered on a given page.
 *
 * ```
 * canShowQuickEdit = hasPageEditorRole(user) && canEdit && isEditablePageSlug(slug)
 * ```
 */
export function canShowQuickEdit({ user, canEdit, slug }: QuickEditGateInput): boolean {
  return hasPageEditorRole(user) && canEdit && isEditablePageSlug(slug)
}
